import React, { useEffect, useRef } from "react";
import { Box, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { CheckIcon as AnimatedCheckIcon, type CheckIconHandle } from "../../../components/ui/check-animated";
import {
  SIMULATOR_RUN_ACCENT,
  SIMULATOR_RUN_ACCENT_GRADIENT,
  SIMULATOR_RUN_STEPS,
} from "./simulatorRunWizardConstants";

function SimulatorRunWizardStepGlyph(props: {
  done: boolean;
  active: boolean;
  accent: string;
  borderColor: string;
  circleBg: string;
}): React.ReactElement {
  const checkRef = useRef<CheckIconHandle>(null);
  const wasDoneRef = useRef(props.done);

  useEffect(() => {
    if (props.done && !wasDoneRef.current) {
      checkRef.current?.startAnimation();
    }
    wasDoneRef.current = props.done;
  }, [props.done]);

  if (props.done) {
    return (
      <Flex
        boxSize="28px"
        borderRadius="full"
        bgGradient={SIMULATOR_RUN_ACCENT_GRADIENT}
        align="center"
        justify="center"
        flexShrink={0}
        color="white"
      >
        <AnimatedCheckIcon ref={checkRef} size={14} aria-hidden />
      </Flex>
    );
  }

  if (props.active) {
    return (
      <Flex
        boxSize="28px"
        borderRadius="full"
        borderWidth="2px"
        borderColor={props.accent}
        bg={props.circleBg}
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Box boxSize="10px" borderRadius="full" bg={props.accent} />
      </Flex>
    );
  }

  return (
    <Flex
      boxSize="28px"
      borderRadius="full"
      borderWidth="1px"
      borderColor={props.borderColor}
      bg="transparent"
      flexShrink={0}
    />
  );
}

export function SimulatorRunWizardStepsHeader(props: {
  step: number;
  subtleText: string;
  borderColor: string;
  cardBg: string;
  cardShadow: string;
  onStepClick: (index: number) => void;
}): React.ReactElement {
  const accent = SIMULATOR_RUN_ACCENT;
  const lineComplete = accent;
  const lineFuture = props.borderColor;

  return (
    <Box
      w="100%"
      mb={{ base: 4, md: 5 }}
      bg={props.cardBg}
      borderWidth="1px"
      borderColor={props.borderColor}
      borderRadius="16px"
      px={{ base: 4, md: 6 }}
      pt={{ base: 4, md: 5 }}
      pb={{ base: 5, md: 6 }}
      boxShadow={props.cardShadow}
      overflow="visible"
      role="navigation"
      aria-label={`Simulation wizard steps, step ${props.step + 1} of ${SIMULATOR_RUN_STEPS.length}`}
    >
      <Grid
        role="list"
        w="100%"
        templateColumns="repeat(5, minmax(0, 1fr))"
        columnGap={{ base: 1, md: 2 }}
      >
        {SIMULATOR_RUN_STEPS.map((s, idx) => {
          const done = idx < props.step;
          const active = idx === props.step;
          const clickable = done;
          const isLast = idx === SIMULATOR_RUN_STEPS.length - 1;
          const segmentComplete = idx < props.step;
          const segmentToNextStep = idx === props.step;
          const reached = done || active;
          const labelColor = reached ? accent : props.subtleText;
          const labelWeight = active ? "semibold" : reached ? "medium" : "normal";
          const colStart = idx * 2 + 1;

          return (
            <React.Fragment key={s.key}>
              <GridItem
                role="listitem"
                colStart={colStart}
                colEnd={colStart + 1}
                rowStart={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifySelf="stretch"
                minW={0}
                aria-current={active ? "step" : undefined}
              >
                <Flex h="28px" align="center" justify="center" w="100%">
                  <Box
                    as="button"
                    type="button"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="100%"
                    h="28px"
                    p={0}
                    m={0}
                    border="none"
                    bg="transparent"
                    cursor={clickable ? "pointer" : "default"}
                    onClick={clickable ? () => props.onStepClick(idx) : undefined}
                    aria-label={s.label}
                    disabled={!clickable}
                    _hover={clickable ? { opacity: 0.88 } : undefined}
                    _disabled={{ cursor: "default", opacity: 1 }}
                  >
                    <SimulatorRunWizardStepGlyph
                      done={done}
                      active={active}
                      accent={accent}
                      borderColor={props.borderColor}
                      circleBg={props.cardBg}
                    />
                  </Box>
                </Flex>
                <Text
                  mt={2}
                  px={{ base: 0.5, md: 1 }}
                  fontSize={{ base: "11px", sm: "xs", md: "sm" }}
                  fontWeight={labelWeight}
                  color={labelColor}
                  fontFamily="'Urbanist', sans-serif"
                  textAlign="center"
                  lineHeight="1.35"
                  w="100%"
                  noOfLines={2}
                >
                  {s.label}
                </Text>
              </GridItem>
              {!isLast ? (
                <GridItem
                  colStart={colStart + 1}
                  colEnd={colStart + 2}
                  rowStart={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minW={0}
                  aria-hidden
                >
                  <Box
                    w="100%"
                    h="2px"
                    maxW="100%"
                    bg={segmentComplete || segmentToNextStep ? lineComplete : lineFuture}
                    borderRadius="full"
                  />
                </GridItem>
              ) : null}
            </React.Fragment>
          );
        })}
      </Grid>
    </Box>
  );
}
